import React, { useRef, useState } from 'react';
import '../css/fileUpload.css';
import { FileUploadIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

export interface FileUploadProps {
  accept?: string;           // e.g. ".jpg,.png,.svg" — defaults to image types
  multiple?: boolean;
  disabled?: boolean;
  supportText?: string;      // e.g. "JPG, PNG or SVG"
  className?: string;
  onFilesSelected?: (files: File[]) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  accept = '.jpg,.jpeg,.png,.svg',
  multiple = false,
  disabled = false,
  supportText = 'JPG, PNG or SVG',
  className = '',
  onFilesSelected,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleContainerClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) inputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || /* istanbul ignore next */ []);
    if (files.length > 0 && onFilesSelected) onFilesSelected(files);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => { //NOSONAR
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && onFilesSelected) onFilesSelected(files);
  };

  const containerClass = [
    'scl--cms-components-fileUpload-container',
    isDragging ? 'scl--cms-components-fileUpload--dragging' : '',
    disabled ? 'scl--cms-components-fileUpload--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div //NOSONAR
      className={containerClass}
      onClick={handleContainerClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleContainerClick();
      }}
      aria-label="Upload file"
    >
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="scl--cms-components-fileUpload-input"
        onChange={handleInputChange}
      />

      {/* Icon Button */}
      <div className="scl--cms-components-fileUpload-iconBtn">
        <FileUploadIcon />
      </div>

      {/* Text block */}
      <div className="scl--cms-components-fileUpload-text">
        {/* Header */}
        <div className="scl--cms-components-fileUpload-header">
          <span className="scl--cms-components-fileUpload-headerText">
            Drag and Drop or
          </span>
          <span //NOSONAR
            className="scl--cms-components-fileUpload-link"
            onClick={handleLinkClick}
            role="button"
            tabIndex={-1}
          >
            choose your file
          </span>
          <span className="scl--cms-components-fileUpload-headerText">
            for upload
          </span>
        </div>

        {/* Support text */}
        <div className="scl--cms-components-fileUpload-support">
          <span className="scl--cms-components-fileUpload-supportText">
            {supportText}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;